import { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { EVENTO_VOLVER, motivoRecarga, operacionesEnCurso, recargarUnaVez } from '../utils/actualizacionApp';

// Identificador del build que corre ahora (vite.config.js lo define y publica /version.json).
// eslint-disable-next-line no-undef
const BUILD_ACTUAL = typeof __BUILD_ID__ !== 'undefined' ? __BUILD_ID__ : '';

async function buildPublicado() {
  try {
    const respuesta = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
    return respuesta.ok ? (await respuesta.json())?.build || null : null;
  } catch {
    return null; // Sin red: se decide solo por el tiempo fuera.
  }
}

// El service worker sirve el HTML desde su caché: hay que esperar a que el nuevo
// quede activo (hasta 8 s) o la recarga volvería a abrir la versión anterior.
async function actualizarServiceWorker() {
  try {
    const registro = await navigator.serviceWorker?.getRegistration?.();
    if (!registro) return;
    await registro.update();
    const nuevo = registro.installing || registro.waiting;
    if (!nuevo || nuevo.state === 'activated') return;
    await new Promise((listo) => {
      const limite = setTimeout(listo, 8000);
      nuevo.addEventListener('statechange', () => {
        if (nuevo.state === 'activated' || nuevo.state === 'redundant') { clearTimeout(limite); listo(); }
      });
    });
  } catch { /* sin service worker o sin red: recargar igual */ }
}

// Espera a que termine cualquier guardado en curso antes de recargar.
async function recargarCuandoSePueda(motivo) {
  await actualizarServiceWorker();
  const intentar = () => {
    if (operacionesEnCurso() > 0) { setTimeout(intentar, 1000); return; }
    if (motivo === 'dia') window.location.replace('/mis-clases');
    else window.location.reload();
  };
  intentar();
}

/**
 * Al volver a la app (Capacitor appStateChange o pestaña visible): busca una versión
 * nueva del service worker y del sitio, y recarga si hay versión nueva, cambió el día
 * o estuvo oculta mucho tiempo. Si no, avisa a las pantallas para refrescar sus datos.
 */
export function useActualizacionApp() {
  useEffect(() => {
    let ocultoDesde = document.visibilityState === 'hidden' ? Date.now() : null;
    let revisando = false;
    const alOcultar = () => { if (!ocultoDesde) ocultoDesde = Date.now(); };
    const alVolver = async () => {
      if (revisando || !ocultoDesde) return;
      revisando = true;
      const desde = ocultoDesde;
      ocultoDesde = null;
      try {
        const motivo = motivoRecarga({ buildActual: BUILD_ACTUAL, buildPublicado: await buildPublicado(),
          ocultoDesde: desde, ahora: Date.now() });
        if (motivo) recargarCuandoSePueda(motivo);
        else window.dispatchEvent(new Event(EVENTO_VOLVER));
      } finally {
        revisando = false;
      }
    };
    const alCambiarVisibilidad = () => (document.visibilityState === 'visible' ? alVolver() : alOcultar());
    // Un archivo de un deploy anterior que ya no existe: recargar una vez en vez de quedar trancada.
    const alFallarCarga = (evento) => { evento.preventDefault?.(); recargarUnaVez(); };

    document.addEventListener('visibilitychange', alCambiarVisibilidad);
    window.addEventListener('vite:preloadError', alFallarCarga);
    let listener;
    if (Capacitor.isNativePlatform()) {
      import('@capacitor/app').then(({ App: CapApp }) => {
        listener = CapApp.addListener('appStateChange', ({ isActive }) => (isActive ? alVolver() : alOcultar()));
      }).catch(() => {});
    }
    return () => {
      document.removeEventListener('visibilitychange', alCambiarVisibilidad);
      window.removeEventListener('vite:preloadError', alFallarCarga);
      listener?.then?.(l => l.remove());
    };
  }, []);
}
